import React, { useEffect, useState } from "react";
import axios from "axios";
import { MapPin, CheckCircle2, XCircle, Loader2, UserRound } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function HomeCollectionSlotPicker({ pincode, onPincodeChange, date, onDateChange, slot, onSlotChange, onServiceability, idPrefix = "hc" }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(false);
  const cleanPin = (pincode || "").replace(/\D/g, "").slice(0, 6);

  useEffect(() => {
    if (cleanPin.length !== 6 || !date) {
      setInfo(null);
      if (onServiceability) onServiceability(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    axios.get(`${API}/home-collection/slots`, { params: { pincode: cleanPin, date } })
      .then((res) => {
        if (cancelled) return;
        setInfo(res.data);
        if (onServiceability) onServiceability(!!res.data.serviceable);
        const slots = res.data.slots || [];
        const current = slots.find((s) => s.time_slot === slot);
        if (!current || current.available <= 0) {
          const first = slots.find((s) => s.available > 0);
          onSlotChange(first ? first.time_slot : "");
        }
      })
      .catch(() => { if (!cancelled) setInfo(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cleanPin, date]);

  return (
    <div className="space-y-3" data-testid={`${idPrefix}-slot-picker`}>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Kolkata PIN Code *</label>
          <div className="relative">
            <MapPin className="w-3.5 h-3.5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              inputMode="numeric"
              maxLength={6}
              value={pincode}
              onChange={(e) => onPincodeChange(e.target.value.replace(/\D/g, "").slice(0, 6))}
              placeholder="700091"
              data-testid={`${idPrefix}-pincode-input`}
              className="w-full pl-8 pr-3 py-2.5 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs font-mono tracking-widest"
            />
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1">Collection Date</label>
          <input
            type="date"
            min={new Date().toISOString().split("T")[0]}
            value={date}
            onChange={(e) => onDateChange(e.target.value)}
            data-testid={`${idPrefix}-date-input`}
            className="w-full px-3 py-2.5 bg-[#FBF9F4] border border-gray-200 rounded-xl text-xs"
          />
        </div>
      </div>

      {cleanPin.length === 6 && (
        <div data-testid={`${idPrefix}-serviceability`} className={`px-3 py-2 rounded-xl text-[11px] flex items-center gap-2 ${loading ? "bg-gray-50 text-gray-500" : info?.serviceable ? "bg-emerald-50 text-emerald-900 border border-emerald-200" : "bg-rose-50 text-rose-800 border border-rose-200"}`}>
          {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : info?.serviceable ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" /> : <XCircle className="w-3.5 h-3.5 text-rose-500" />}
          {loading ? "Checking phlebotomist availability…" : info?.serviceable
            ? <span><b>{info.area}</b> · {info.zone} · {info.phlebotomists_count} phlebotomist{info.phlebotomists_count === 1 ? "" : "s"} on roster</span>
            : (info?.message || "PIN code not serviceable yet")}
        </div>
      )}

      {info?.serviceable && (
        <div>
          <label className="block text-xs font-semibold text-gray-700 mb-1.5">Live Slots (instant confirmation)</label>
          <div className="grid grid-cols-2 gap-2" data-testid={`${idPrefix}-slot-grid`}>
            {info.slots.map((s) => {
              const full = s.available <= 0;
              const active = slot === s.time_slot;
              return (
                <button
                  key={s.time_slot}
                  type="button"
                  disabled={full}
                  onClick={() => onSlotChange(s.time_slot)}
                  data-testid={`${idPrefix}-slot-${s.time_slot.slice(0, 5).replace(":", "")}`}
                  className={`p-2.5 rounded-xl border text-left transition-all ${full ? "opacity-50 cursor-not-allowed bg-gray-50 border-gray-200" : active ? "border-[#0D3B2E] bg-[#EBF4F1] ring-1 ring-[#0D3B2E]" : "border-gray-200 hover:border-[#3B8271]"}`}
                >
                  <p className="text-[11px] font-bold text-gray-900">{s.time_slot}</p>
                  <p className={`text-[10px] font-semibold ${full ? "text-gray-400" : s.available === 1 ? "text-amber-700" : "text-emerald-700"}`}>
                    {full ? "Fully booked" : `${s.available} slot${s.available === 1 ? "" : "s"} left`}
                  </p>
                </button>
              );
            })}
          </div>
          {slot && (
            <p className="mt-2 text-[10px] text-gray-500 flex items-center gap-1">
              <UserRound className="w-3 h-3" /> Phlebotomist on duty: {(info.slots.find((s) => s.time_slot === slot)?.phlebotomists || []).join(", ")}
            </p>
          )}
        </div>
      )}
    </div>
  );
}
